import { type FC } from 'react';
import dayjs from 'dayjs';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { UiStack, UiText, uiStyles, type UiStackProps } from '@/lib/ui';
import { type FieldMetadata } from '@/base/FormBuilder/Field';
import BaseBlockList from '@/base/BlockList';

interface InformationListProps {
  fieldsMetadata: FieldMetadata[] | undefined
  response: Record<string, any>
}

const InformationList: FC<InformationListProps> = ({ fieldsMetadata, response }) => {
  return (
    <BaseBlockList
      bgColor={'#fff'}
      enableDivider={true}
      shadow={uiStyles.cardShadow}
    >
      {fieldsMetadata
        ?.filter(fieldMetadata => { return fieldMetadata.type !== 'sectionBreak'; })
        ?.map((fieldMetadata) => {
          return (
            <InformationItem
              key={fieldMetadata.id}
              fieldMetadata={fieldMetadata}
              response={response} />
          );
        })}
    </BaseBlockList>
  );
};

interface InformationItemProps extends UiStackProps {
  fieldMetadata: FieldMetadata
  response: Record<string, any>
}

const InformationItem: FC<InformationItemProps> = ({
  fieldMetadata,
  response,
  ...props
}) => {
  if (fieldMetadata.type === 'input') {
    return (
      <UiStack spacing={0} px={8} py={4} {...props}>
        <UiText variant={'body2'} color={'text.secondary'}>{fieldMetadata.label ?? ''}</UiText>
        <UiText>{response[fieldMetadata.id]}</UiText>
      </UiStack>
    );
  }
  if (fieldMetadata.type === 'select') {
    return (
      <UiStack spacing={0} px={8} py={4} {...props}>
        <UiText variant={'body2'} color={'text.secondary'}>{fieldMetadata.label ?? ''}</UiText>
        <UiText>{response[fieldMetadata.id]}</UiText>
      </UiStack>
    );
  }
  if (fieldMetadata.type === 'date') {
    return (
      <UiStack spacing={0} px={8} py={4} {...props}>
        <UiText variant={'body2'} color={'text.secondary'}>{fieldMetadata.label ?? ''}</UiText>
        <UiText>{dayjs(response[fieldMetadata.id] as string).format('DD MMM YYYY')}</UiText>
      </UiStack>
    );
  }
  if (fieldMetadata.type === 'descriptionBlock') {
    return (
      <UiStack spacing={0} px={8} py={4} {...props}>
        <UiText>{fieldMetadata.description}</UiText>
      </UiStack>
    );
  }

  if (fieldMetadata.type === 'phone') {
    const responseValue = response[fieldMetadata.id];
    const phoneNumber = parsePhoneNumberFromString(responseValue ?? '');
    return (
      <UiStack spacing={0} px={8} py={4} {...props}>
        <UiText variant={'body2'} color={'text.secondary'}>{fieldMetadata.label ?? ''}</UiText>
        <UiText>{phoneNumber ? phoneNumber.formatInternational() : responseValue}</UiText>
      </UiStack>
    );
  }
  return undefined;
};

export default InformationList;
